import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import './GuidePage.css';
import Navigation from './Navigation';
import { usePageTracking } from './hooks/useAnalytics';

function TimegrapherGuidePage() {
  usePageTracking('timegrapher_guide', {
    page_title: 'What Is a Timegrapher? Readings Explained'
  });

  return (
    <div className="guide-page">
      <Helmet>
        <title>What Is a Timegrapher? Readings Explained | ChronoLog</title>
        <meta name="description" content="A timegrapher listens to a mechanical watch's ticks and reports rate, amplitude, and beat error. Here's what each reading means and what's healthy." />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="What Is a Timegrapher? Readings Explained" />
        <meta property="og:description" content="A timegrapher listens to a mechanical watch's ticks and reports rate, amplitude, and beat error. Here's what each reading means and what's healthy." />
        <meta property="og:url" content="https://www.chronolog.watch/what-is-a-timegrapher" />
        <meta property="og:image" content="https://www.chronolog.watch/og-image.png" />
        <link rel="canonical" href="https://www.chronolog.watch/what-is-a-timegrapher" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Article",
                "headline": "What Is a Timegrapher and What Do Its Readings Mean?",
                "description": "A plain-language explanation of what a timegrapher does and how to read its three outputs — rate, amplitude, and beat error — including healthy ranges and how hardware machines compare with phone apps.",
                "author": { "@type": "Organization", "name": "ChronoLog" },
                "publisher": { "@type": "Organization", "name": "ChronoLog", "url": "https://www.chronolog.watch" },
                "url": "https://www.chronolog.watch/what-is-a-timegrapher",
                "datePublished": "2026-10-12",
                "keywords": "timegrapher, timegrapher readings, watch rate, amplitude, beat error, mechanical watch accuracy, timegrapher app"
              },
              {
                "@type": "FAQPage",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": "Can a phone be used as a timegrapher?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes. Timegrapher apps use the phone's built-in microphone to pick up the ticking of the escapement through the air and extract rate and beat error from it. ChronoLog's audio timegrapher works this way, with typical rate accuracy of ±2-5 seconds per day and no extra hardware — compared with ±0.1-0.5 s/day for a dedicated machine with a contact sensor. Phone apps need a quiet room, and amplitude is estimated at best rather than directly measured."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "What is a good amplitude for a mechanical watch?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Roughly 250-315 degrees on a fully wound watch measured dial-up, though published guidance varies by source and movement. Amplitude normally drops in vertical positions and as the mainspring unwinds; staying above about 200 degrees after 24 hours of running is a common benchmark. A watch that cannot reach about 200 degrees fully wound usually needs a service. The reading is only meaningful if the correct lift angle for the movement is set on the timegrapher."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "What does beat error mean?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Beat error is the asymmetry between the tick and the tock, measured in milliseconds. The balance wheel should swing equally far on each side of its resting point; when the impulse jewel sits off-center, one half of the oscillation takes longer than the other. A beat error of 0.5 ms or less is good, and under about 1 ms is acceptable for most modern movements. Large beat errors waste energy and are a sign the movement needs adjustment by a watchmaker."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "How much does a timegrapher cost?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Entry-level hobbyist machines such as the widely sold Weishi units typically cost around $100-250. Professional bench timegraphers from makers like Witschi run $500 to $3,000 or more. Phone apps cost a few dollars and measure rate well through the microphone, though they are less precise than contact-sensor hardware and estimate amplitude at best."
                    }
                  }
                ]
              }
            ]
          })}
        </script>
      </Helmet>

      <Navigation showAndroidButton={false} />

      <article className="article">
        <h1>What Is a Timegrapher and What Do Its Readings Mean?</h1>
        <div className="article-meta">October 2026</div>

        <p className="article-lead">
          A timegrapher is an instrument that listens to a mechanical watch's ticking and turns it into three numbers: rate (how many seconds per day the watch gains or loses), amplitude (how energetically the balance wheel swings), and beat error (how evenly the watch ticks). Watchmakers use one to regulate and diagnose movements; collectors use one to check whether a watch is running within spec. A reading takes seconds and reveals more about a movement's health than days of wearing the watch.
        </p>

        <h2>What a timegrapher does</h2>

        <p>
          Every mechanical watch has an escapement — the assembly that releases the mainspring's energy in tiny, regular increments. Each release makes a sound: the familiar tick. How often it ticks is set by the movement's beat rate, quoted in beats per hour (BPH). The most common modern rate is 28,800 BPH, which works out to 8 ticks per second, one every 125 milliseconds. Other standard rates range from 18,000 BPH in vintage movements to 36,000 BPH in high-beat calibers.
        </p>

        <p>
          A timegrapher picks up those ticks — traditionally with a piezoelectric contact sensor clamped against the watch case — and times each one against a precise quartz reference. From the spacing and internal structure of the ticks it computes the three readings below, and plots each tick as a dot on a scrolling trace. A clean movement draws two tight parallel lines; problems show up as slope, scatter, or splayed lines.
        </p>

        <p>
          The key thing to understand is that a timegrapher gives you an instantaneous diagnosis, not a verdict on real-world timekeeping. It tells you how the movement is running right now, in one position, at one state of wind. How the watch actually performs on your wrist over a week is a related but different question — see <Link to="/accuracy">how accurate a mechanical watch should be</Link> for what counts as normal there.
        </p>

        <h2>The three readings: rate, amplitude, and beat error</h2>

        <h3>Rate</h3>

        <p>
          Rate is the deviation from perfect timekeeping, expressed in seconds per day. A reading of +8 means that if the watch kept behaving exactly as it is behaving this instant, it would gain 8 seconds over 24 hours. Rate changes with position — dial-up and crown-down readings often differ by several seconds — which is why watchmakers measure in multiple positions and why chronometer standards specify positional testing. COSC, the Swiss chronometer certification body, requires an average daily rate between −4 and +6 seconds; an ordinary modern automatic is typically regulated somewhere within about ±15 seconds per day, with specs varying widely by brand and caliber.
        </p>

        <h3>Amplitude</h3>

        <p>
          Amplitude is how far the balance wheel rotates on each swing, measured in degrees. It is effectively a fuel gauge for the movement's energy: a healthy, fully wound movement typically swings somewhere around 250–315° measured dial-up (published guidance varies by source and movement), and amplitude naturally drops in vertical positions and as the mainspring winds down. Low amplitude — consistently under about 200° on a full wind — usually means degraded lubricants or worn parts, and is the classic sign a service is due. Very high amplitude (roughly 330° and up) risks "knocking," where the balance swings so far it strikes the impulse pin from the wrong side.
        </p>

        <p>
          One caveat: a timegrapher does not see the balance wheel. It infers amplitude from the timing of sounds within each tick, using the movement's lift angle — a per-caliber constant, commonly around 52° but ranging from roughly 44° to 58°. If the lift angle setting is wrong, the amplitude reading is wrong, even though rate and beat error remain valid.
        </p>

        <h3>Beat error</h3>

        <p>
          Beat error is the asymmetry between the tick and the tock, in milliseconds. The balance should swing equally far each side of its resting point, so the two halves of each oscillation take equal time. When the impulse jewel is off-center, one interval runs long and the other short — at 28,800 BPH, perhaps 126 ms then 124 ms instead of 125 and 125. Under 0.5 ms is good; under about 1 ms is acceptable on most modern movements. For what causes it and when it matters, see the full guide to <Link to="/beat-error">watch beat error</Link>.
        </p>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Reading</th>
                <th>What it tells you</th>
                <th>Typical healthy range</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Rate</td>
                <td>Seconds gained or lost per day, right now</td>
                <td>Within ~±15 s/day for a modern automatic; −4/+6 s/day for a COSC chronometer</td>
              </tr>
              <tr>
                <td>Amplitude</td>
                <td>Balance wheel swing, in degrees — the movement's energy health</td>
                <td>~250–315° fully wound (dial-up); above ~200° after 24 hours running</td>
              </tr>
              <tr>
                <td>Beat error</td>
                <td>Timing asymmetry between tick and tock, in milliseconds</td>
                <td>0–0.5 ms good; under ~1 ms acceptable for most modern movements</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2>Hardware timegraphers vs phone apps</h2>

        <p>
          Dedicated machines span a wide price range. Entry-level units — the ubiquitous Weishi machines and similar — typically cost around $100–250 and are perfectly serviceable for hobbyists. Professional bench instruments from makers like Witschi run $500 to $3,000 and up, with faster convergence, positional test automation, and certified precision. All of them share the same advantage: a contact sensor pressed against the case, which delivers a strong, clean signal that is essentially immune to room noise.
        </p>

        <p>
          Phone apps take the harder path: the built-in microphone, listening to the tick through open air. The signal is dramatically weaker and buried in ambient noise, which is a genuine signal-processing problem rather than a packaging difference. ChronoLog's audio timegrapher solves it with epoch folding, a technique borrowed from radio astronomy, and measures rate and beat error through the phone's microphone with typical accuracy of ±2–5 seconds per day, no extra hardware — there is a full engineering write-up on <Link to="/timegrapher">how we built a timegrapher from a phone microphone</Link>.
        </p>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Hardware timegrapher</th>
                <th>Phone app</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Price</td>
                <td>~$100–250 entry level; $500–3,000+ professional</td>
                <td>Usually under $10</td>
              </tr>
              <tr>
                <td>Sensor</td>
                <td>Piezoelectric contact sensor clamped to the case</td>
                <td>Built-in microphone, through the air</td>
              </tr>
              <tr>
                <td>Rate accuracy</td>
                <td>~±0.1–0.5 s/day</td>
                <td>Typically ±2–5 s/day</td>
              </tr>
              <tr>
                <td>Amplitude</td>
                <td>Measured from tick timing via lift angle</td>
                <td>Estimated at best; often unavailable</td>
              </tr>
              <tr>
                <td>Beat error</td>
                <td>Precise</td>
                <td>Approximate</td>
              </tr>
              <tr>
                <td>Ambient noise</td>
                <td>Largely immune</td>
                <td>Needs a quiet room</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p>
          The practical takeaway: for regulating a movement on the bench, hardware wins. For answering "is my watch healthy, and roughly how fast or slow is it running?", an app on the phone already in your pocket is usually enough.
        </p>

        <h2>How to take a reading you can trust</h2>

        <p>
          Timegrapher numbers are only as good as the conditions you take them in. A few habits make the difference between a meaningful reading and a misleading one:
        </p>

        <p>
          <strong>Wind the watch fully</strong> and let it run for a few minutes before measuring. Amplitude and rate both sag as the mainspring unwinds, so a half-wound reading will look worse than the movement deserves.
        </p>

        <p>
          <strong>Keep the environment quiet and the watch still.</strong> This matters most for microphone-based apps, but even contact sensors dislike vibration from a wobbly desk or a running appliance.
        </p>

        <p>
          <strong>Set the correct lift angle</strong> for your movement if you care about the amplitude number. Most machines default to 52°, which is right for many modern calibers and wrong for plenty of others — the figure is usually published in the movement's technical sheet.
        </p>

        <p>
          <strong>Measure in more than one position.</strong> Dial-up, dial-down, and crown-down is a reasonable minimum. Give the trace 20–30 seconds to stabilize after each position change, and expect some spread between positions — a few seconds per day of positional variation is normal.
        </p>

        <p>
          <strong>Don't stop at one snapshot.</strong> A single reading tells you how the movement is running at that instant; your watch's true daily rate is the average over how you actually wear and rest it. Tracking <Link to="/measure-watch-accuracy">measured accuracy over several days</Link> complements the timegrapher's instant diagnosis.
        </p>

        <h2>Limits: what a timegrapher can't tell you</h2>

        <p>
          A timegrapher is a diagnostic tool, not an oracle. Its reading is a snapshot in a handful of static positions at one temperature and state of wind — it will not match the rate you observe on the wrist, where the watch moves through every position and your arm keeps the mainspring topped up. A watch can look mediocre on the machine and keep excellent time in real life, and occasionally the reverse.
        </p>

        <p>
          It also identifies symptoms rather than causes. A suddenly fast rate with a clean trace suggests magnetization; scattered dots suggest dirt or damage in the escapement; low amplitude suggests old oil — but these are inferences, and confirming the cause is a job for a watchmaker with the movement open on the bench. The machine cannot measure power reserve directly, cannot check water resistance, and a conventional acoustic timegrapher cannot read a quartz watch at all, since quartz movements do not have a ticking escapement of the same kind.
        </p>

        <p>
          Finally, remember the lift-angle caveat: of the three readings, amplitude is the only computed one, and it is only as trustworthy as the lift angle you entered. Rate and beat error come straight from tick timing and are robust even when the amplitude figure is garbage.
        </p>

        <div className="article-cta">
          <h2>Check your watch without the hardware</h2>
          <p>
            ChronoLog measures your watch's rate against atomic time and includes an audio timegrapher that reads rate and beat error through your phone's microphone — $5.99 one-time on the <a href="https://apps.apple.com/us/app/chronolog-watch-accuracy/id6452083510">App Store</a>.
          </p>
        </div>

        <div className="related-guides">
          <h2>Related guides</h2>
          <ul>
            <li><Link to="/accuracy">How accurate should a mechanical watch be?</Link></li>
            <li><Link to="/beat-error">Watch beat error explained</Link></li>
            <li><Link to="/measure-watch-accuracy">How to measure watch accuracy at home</Link></li>
            <li><Link to="/timegrapher">Building a timegrapher from a phone microphone</Link></li>
          </ul>
        </div>
      </article>
    </div>
  );
}

export default TimegrapherGuidePage;
